import { useEffect, useRef } from "react"

import type { ChartData } from "@/app/report/common/constructor"
import { VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"
import {
  coerceVerticalAxisTypes,
  drawVerticalPlayerChart,
  rowHeightFor,
} from "@/app/report/common/chart/verticalPlayerChart"
import { resolveGraphShowAxis } from "@/app/report/common/sections/graph"

import "../verticalPlayerChart.scss"

const DEFAULT_CHART_WIDTH = 880

/**
 * 리포트 PDF 의 Chart by Player 세로 차트 한 묶음(페이지) — 화면 컴포넌트와 같은 공용 렌더러로 그린다 (#185).
 * 여러 묶음이 같은 스케일을 쓰도록 PrintableChartByPlayerSection 이 전체 최대값(highest·highestStackedSum)을 넘긴다.
 */
export default function PrintableVerticalPlayerChart({
  chartData,
  scaleRatio = 1,
  decimalPoint = 1,
  chartAnnotation = VALUE_POSITION_TYPE.HIDE_VALUES,
  highest,
  highestStackedSum,
}: {
  chartData: ChartData
  scaleRatio?: number
  decimalPoint?: number
  chartAnnotation?: VALUE_POSITION_TYPE
  highest?: number[]
  highestStackedSum?: number
}) {
  const translateKey = useTranslateMetricKey()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const width = el.offsetWidth || DEFAULT_CHART_WIDTH * scaleRatio
    drawVerticalPlayerChart(el, {
      rows: chartData.data,
      axisY: coerceVerticalAxisTypes(chartData.axisY),
      width,
      showAxis: resolveGraphShowAxis(chartData.settings),
      rowHeight: rowHeightFor(coerceVerticalAxisTypes(chartData.axisY)) * scaleRatio,
      fontSize: 11 * scaleRatio,
      valuePositionType: chartAnnotation,
      decimalPoint,
      highest,
      highestStackedSum,
    })
  }, [chartData, scaleRatio, decimalPoint, chartAnnotation, highest, highestStackedSum])

  return (
    <div className="VerticalPlayerChart VerticalPlayerChart--pdf">
      <div className="VerticalPlayerChart__legend" style={{ fontSize: 12 * scaleRatio }}>
        {chartData.axisY.map((axis) => (
          <div
            key={axis.key}
            className="VerticalPlayerChart__legend-item"
            style={{ color: axis.color }}>
            <span>●</span>
            <span>{translateKey(axis.key) ?? axis.name}</span>
          </div>
        ))}
      </div>
      <div ref={containerRef} className="VerticalPlayerChart__canvas" />
    </div>
  )
}
